import React, { useState, useEffect } from 'react';
import { bool, string } from 'prop-types';
import { Heading } from '@thd-olt-component-react/heading';
import { ComponentPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import {
  useDataModel
} from '@thd-nucleus/data-sources';

import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withLayoutGroup, useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import DesktopContent from './components/DesktopContent/DesktopContent';
import { RichContent } from './components/RichContent/RichContent';
import './product-overview.scss';
import {
  defaultThemeProperties,
  getBullets,
  isEventBusCreated,
  isRichContentExpandable, productOverviewDefaultProps, productOverviewPropTypes,
  setRichContentExpandable, themeProperties
} from './helpers';
import { dataModel } from './dataModel';

let setRichContentExpandableTimer = null;

const removeRichContentExpandableTimer = () => {
  clearInterval(setRichContentExpandableTimer);
};

export const ProductOverviewDesktopComponent = (props) => {
  const {
    headingTitle,
    itemId,
    showRichContentsOnly,
    hideRichContents,
    preview,
    showReturnMessage,
    showPla: showPlaFromProps
  } = props;

  const { showSponsoredPla = false } = useLayoutGroupProps({ props });
  const showPlaFromLayoutProps = showSponsoredPla;

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });
  const richContentsOnlyRef = React.createRef();
  const [shouldShowMore, setShouldShowMore] = useState(false);

  const richContentsOnlyOnImageComplete = () => {
    removeRichContentExpandableTimer();
    if (isRichContentExpandable(richContentsOnlyRef)) {
      setShouldShowMore(true);
    }
  };

  useEffect(() => {
    setRichContentExpandable({
      ref: richContentsOnlyRef,
      timer: setRichContentExpandableTimer,
      richContentsOnlyOnImageComplete,
      removeRichContentExpandableTimer
    });
    if (isEventBusCreated()) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-overview.ready', {});
    }
    return () => {
      removeRichContentExpandableTimer();
    };
  }, [itemId]);

  if (loading) {
    return (
      <ComponentPlaceholder
        height={2288}
        rows={20}
        color="#E0E0E0"
        showLoadingAnimation
      />
    );
  }

  if ((error && !data) || !data?.product) {
    return null;
  }

  const {
    modelNumber,
    rentalCategory,
    rentalSubCategory,
    storeSkuNumber,
    upc
  } = data?.product?.identifiers || {};

  const {
    richContent
  } = data?.product?.media || {};

  const {
    prop65Warning,
    returnable
  } = data?.product?.info || {};

  const {
    description,
    highlights,
    infoAndGuides: documents,
    descriptiveAttributes
  } = data?.product?.details || {};

  const bullets = getBullets({
    channel: 'desktop',
    descriptiveAttributes,
    showRichContentsOnly,
    showReturnMessage,
    prop65Warning,
    returnable
  });

  if (showRichContentsOnly) {
    return (
      <RichContent
        itemId={itemId}
        description={description}
        documents={documents}
        richContent={richContent}
        bullets={bullets}
        showRichContentsOnly={showRichContentsOnly}
        hideRichContents={hideRichContents}
        preview={preview}
        shouldShowMore={shouldShowMore}
        richContentsOnlyRef={richContentsOnlyRef}
        setShouldShowMore={setShouldShowMore}
        channel="desktop"
      />
    );
  }

  return (
    <div
      className="grid pip-row section product-overview"
      data-section="product-overview"
    >
      <Heading title={headingTitle} underline />
      <DesktopContent
        itemId={itemId}
        description={description}
        documents={documents}
        richContent={richContent}
        bullets={bullets}
        hideRichContents={hideRichContents}
        preview={preview}
        showRichContentsOnly={showRichContentsOnly}
        shouldShowMore={shouldShowMore}
        channel="desktop"
        returnMessage={returnable}
        showReturnMessage={showReturnMessage}
        showPla={showPlaFromLayoutProps || showPlaFromProps}
      />
    </div>
  );
};

ProductOverviewDesktopComponent.displayName = 'ProductOverviewDesktop';

ProductOverviewDesktopComponent.dataModel = dataModel;

ProductOverviewDesktopComponent.propTypes = productOverviewPropTypes;

ProductOverviewDesktopComponent.defaultProps = productOverviewDefaultProps;

ProductOverviewDesktopComponent.themeProps = themeProperties;

ProductOverviewDesktopComponent.defaultThemeProps = defaultThemeProperties;

ProductOverviewDesktopComponent.dataModel = dataModel;

const HydratedComponent = withHydrator({
  delay: 1500,
  id: 'product-section-overview',
  placeholder: (
    <ComponentPlaceholder
      height={2288}
      rows={20}
      color="#E0E0E0"
      showLoadingAnimation
    />
  ),
  scrollBuffer: 750,
}, ProductOverviewDesktopComponent);

const DynamicComponent = withDynamicComponent(HydratedComponent);
const ProductOverviewDesktop = withLayoutGroup({
  name: 'product-overview'
}, DynamicComponent);

ProductOverviewDesktop.displayName = 'ProductOverviewDesktop';

ProductOverviewDesktop.dataModel = dataModel;

ProductOverviewDesktop.propTypes = productOverviewPropTypes;

ProductOverviewDesktop.defaultProps = productOverviewDefaultProps;

ProductOverviewDesktop.themeProps = themeProperties;

ProductOverviewDesktop.defaultThemeProps = defaultThemeProperties;

ProductOverviewDesktop.dataModel = dataModel;

export { ProductOverviewDesktop };
